// How many days a stretch of leave costs.
//
// This lived inside MyInfoPage, which was fine while My Leave was the only
// place leave could be entered. A manager recording an absence for somebody in
// their team cannot use that screen, so a second form now exists on Holiday &
// Absence Requests, and a second copy of this arithmetic is exactly what El's
// item 29 was about: two request forms had drifted, and the one that did not
// know the person's FTE filled in a figure the other would never produce for
// the same dates. One function, imported by both.
//
// The server recalculates all of it on the way in, so a screen that disagrees
// only ever produces a refusal the person cannot act on.

// Fixed dates, so a form can count days before the real table has loaded. The
// server counts against bank_holidays, which is the one that decides.
export const UK_BANK_HOLIDAYS_FALLBACK = new Set([
  '2025-01-01','2025-04-18','2025-04-21','2025-05-05','2025-05-26','2025-08-25','2025-12-25','2025-12-26',
  '2026-01-01','2026-04-03','2026-04-06','2026-05-04','2026-05-25','2026-08-31','2026-12-25','2026-12-28',
  '2027-01-01','2027-03-26','2027-03-29','2027-05-03','2027-05-31','2027-08-30','2027-12-27','2027-12-28',
]);

// No dates off at all: every weekday in the range counts.
export const EMPTY_HOLIDAYS = new Set();

// The unit leave is booked in. Matches toHalfDay() on the server, which every
// entitlement figure goes through, so a form cannot suggest a number the
// allowance can never quite cover.
export const toHalfDay = d => Math.round(Number(d || 0) * 2) / 2;

// Sickness is a zero-total counter row (the server's validateLeaveAllowance):
// a count of days off sick, not an allowance. Read as one, a recorded sick day
// showed as holiday booked, a day off Remaining, and -1d of sick leave left.
// Other leave is a count too, unless HR has set an allowance for it.
const isCountOnly = a => a.leave_type === 'sick' || (a.leave_type === 'other' && !(a.total_days > 0));

// Days left on one allowance row; null for a count, which has none.
export const allowanceLeft = a => isCountOnly(a) ? null : (a.total_days || 0) - (a.booked_days || 0);

// Approved days off, sickness aside: the total under a person's requests.
// Other leave is time off and stays in, counted allowance or not.
export const approvedLeaveDays = requests => requests
  .filter(r => r.status === 'approved' && r.leave_type !== 'sick')
  .reduce((s, r) => s + (r.days || 0), 0);

// The holiday year runs 1 MARCH to the end of February, the same rule the
// server applies in holidayYearOf(). My Leave once used an April boundary, so
// for the whole of March it asked for one year's allowance while an approval
// wrote into another's, and the days looked like they had gone missing.
export function holidayYearOf(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  const yr = d.getFullYear();
  const mo = d.getMonth() + 1;
  return mo >= 3 ? `${yr}-${String(yr + 1).slice(-2)}` : `${yr - 1}-${String(yr).slice(-2)}`;
}

// The Total Entitlement / Booked / Remaining figures on My Leave.
export function allowanceTotals(allowances) {
  const capped = allowances.filter(a => !isCountOnly(a));
  const entitlement = capped.reduce((s, a) => s + (a.total_days || 0), 0);
  const booked = capped.reduce((s, a) => s + (a.booked_days || 0), 0);
  return { entitlement, booked, remaining: entitlement - booked };
}

// Every calendar date from start to end inclusive, as YYYY-MM-DD. Stepped in
// UTC throughout: new Date('YYYY-MM-DD') is UTC midnight, and stepping it with
// the local setDate() drifts an hour when the clocks change, so in a UK browser
// a range ending on the Monday after the clocks went back lost that Monday.
export function eachDateInRange(start, end) {
  if (!start || !end) return [];
  const s = new Date(start), e = new Date(end);
  if (isNaN(s) || isNaN(e) || e < s) return [];
  const out = [];
  for (let d = new Date(s); d <= e; d.setUTCDate(d.getUTCDate() + 1)) {
    out.push(d.toISOString().slice(0, 10));
  }
  return out;
}

// The weekdays a part-timer works, as HR records them on the person: weekday
// names in week order ('mon,wed,fri'), or null when nobody has said. Weekends
// are never working days here.
const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri'];

export function normaliseWorkingDays(value) {
  const given = new Set((Array.isArray(value) ? value : String(value ?? '').split(','))
    .map(d => String(d).trim().toLowerCase()));
  const days = WEEKDAYS.filter(d => given.has(d));
  return days.length ? days.join(',') : null;
}

// The same pattern as getUTCDay() numbers, or null when none is recorded.
export function workingWeekdays(pattern) {
  const days = normaliseWorkingDays(pattern);
  return days ? new Set(days.split(',').map(d => WEEKDAYS.indexOf(d) + 1)) : null;
}

export function calcWorkingDays(start, end, fte = 1, holidays = UK_BANK_HOLIDAYS_FALLBACK, workingDays = null) {
  const dates = eachDateInRange(start, end);
  if (!dates.length) return '';
  const pattern = workingWeekdays(workingDays);
  let count = 0;
  for (const iso of dates) {
    const day = new Date(iso).getUTCDay();
    const works = pattern ? pattern.has(day) : day !== 0 && day !== 6;
    if (works && !holidays.has(iso)) count++;
  }
  // A recorded pattern is exact: every day in it is a whole day off work.
  //
  // Without one, the range is costed at the FTE average, to the nearest half day,
  // which is the unit leave is booked in. It used to be one decimal place, so
  // three days at three-fifths came out as 1.8: a figure the box accepted, the
  // allowance was charged for, and nobody could ever land on again. Costed on the
  // whole range rather than per day, so a working week for somebody on three days
  // a week is 3 and not 2.5.
  const result = pattern ? count : toHalfDay(count * Math.min(1, fte || 1));
  return result > 0 ? result : '';
}

// The figure a form should show: the working days in the range, less half a day
// for each end a marker applies to. Never below half a day, because a request
// that exists at all is worth at least that.
//
// Bank holidays are skipped for everybody. A part-timer's allowance does include
// them, but the leave year job books each one for them as a request of its own
// (costed by their working days), so a range that counted one again would charge
// it twice. The server skips them too.
export function leaveDaysFor({ start, end, fte = 1, workingDays = null, bankHolidays = UK_BANK_HOLIDAYS_FALLBACK, startHalf = 'full', endHalf = 'full' }) {
  const notBooked = bankHolidays;
  const days = calcWorkingDays(start, end, fte, notBooked, workingDays);
  if (days === '') return '';
  // A half-day marker only trims an end that was counted at all, as the server
  // does: an afternoon start on a day somebody does not work takes nothing off.
  const counted = d => calcWorkingDays(d, d, 1, notBooked, workingDays) !== '';
  const trim = (startHalf === 'pm' && counted(start) ? 0.5 : 0) + (endHalf === 'am' && counted(end) ? 0.5 : 0);
  return Math.max(days - trim, 0.5);
}

// A change the person has asked for to their approved leave, waiting for the
// approver: the request as it would read, or null when nothing is waiting.
// The server stores it as JSON beside the approved values, and leaves it off
// the rows it sends to anybody outside the person's line.
export function proposedChange(r) {
  try { return r?.proposed_change ? JSON.parse(r.proposed_change) : null; } catch { return null; }
}
